import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { publisherResultImportsApi } from '@/api/publisher-result-imports';
import { reportsApi } from '@/api/reports';
import { ReportData as Report } from '@/components/preview/types';
import { PageShell } from '@/components/system';
import { useConfirmDelete } from '@/hooks/use-confirm-delete';
import { ENTITLEMENTS_QUERY_KEY } from '@/hooks/use-entitlements';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { canExportReport } from '@/utils';
import { downloadBlob, getReportDocxFilename } from '@/utils/download';
import { pageVariants } from './animations';
import { DashboardFilters } from './components/DashboardFilters';
import { DashboardHero } from './components/DashboardHero';
import { DashboardPagination } from './components/DashboardPagination';
import { DashboardReportList } from './components/DashboardReportList';
import { DashboardStatsGrid } from './components/DashboardStats';
import { REPORTS_PER_PAGE } from './constants';
import { DashboardStats, ViewMode } from './types';

const getErrorStatus = (error: unknown) => {
    if (isAxiosError(error)) return error.response?.status;

    if (typeof error === 'object' && error && 'status' in error) {
        return (error as { status?: number }).status;
    }

    return undefined;
};

export default function Dashboard() {
    const confirmDelete = useConfirmDelete();
    const exportReportAccess = useFeatureEntitlement('reports.export');
    const publisherResultImportsFeature = useFeatureFlag('publisherResultImports.reviewFlow');

    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('in_progress');
    const [currentPage, setCurrentPage] = useState(1);
    const [viewMode, setViewMode] = useLocalStorage<ViewMode>('dashboard_view_mode', 'list');

    const normalizedSearch = searchQuery.trim().toLowerCase();
    const hasLocalFilters = Boolean(normalizedSearch) || statusFilter !== 'all';

    const {
        data: paginatedResponse,
        isLoading: isPaginatedLoading,
        isFetching: isPaginatedFetching,
    } = useQuery({
        queryKey: ['reports', 'page', currentPage],
        queryFn: () =>
            reportsApi.list({
                page: currentPage,
            }),
        enabled: !hasLocalFilters,
    });

    const {
        data: allReports = [],
        isLoading: isAllReportsLoading,
        isFetching: isAllReportsFetching,
    } = useQuery<Report[]>({
        queryKey: ['reports', 'all'],
        queryFn: async () => {
            return await reportsApi.listAll();
        },
        enabled: hasLocalFilters,
    });

    const { data: summary } = useQuery({
        queryKey: ['reports', 'summary'],
        queryFn: () => reportsApi.summary(),
    });

    const deleteReportMutation = useMutation({
        mutationFn: (id: string) => reportsApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['reports'] });
            toast.success('Laudo excluído com sucesso');
        },
        onError: () => {
            toast.error('Falha ao excluir laudo');
        },
    });

    const reports = useMemo(() => paginatedResponse?.data ?? [], [paginatedResponse?.data]);
    const serverPagination = paginatedResponse?.pagination;
    const pageSize = serverPagination?.pageSize ?? REPORTS_PER_PAGE;

    const filteredReports = useMemo(() => {
        if (!hasLocalFilters) return reports;

        return allReports.filter((report) => {
            const searchableFields = [
                report.patient?.full_name,
                report.customizations?.template?.name,
                report.tests?.test_name,
                report.tests?.section_title,
            ].filter((field): field is string => Boolean(field));

            const matchesSearch =
                !normalizedSearch ||
                searchableFields.some((field) => field?.toLowerCase().includes(normalizedSearch));

            const matchesStatus = statusFilter === 'all' || report.status === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [allReports, hasLocalFilters, normalizedSearch, reports, statusFilter]);

    const totalPages = hasLocalFilters
        ? Math.ceil(filteredReports.length / pageSize)
        : (serverPagination?.totalPages ?? 0);

    const totalReports = hasLocalFilters
        ? filteredReports.length
        : (serverPagination?.totalCount ?? 0);

    const paginatedReports = hasLocalFilters
        ? filteredReports.slice((currentPage - 1) * pageSize, currentPage * pageSize)
        : reports;
    const visibleReports = paginatedReports;
    const visiblePatientIdsWithInProgressReports = useMemo(() => {
        const patientIds = visibleReports
            .filter((report) => report.status === 'in_progress')
            .map((report) => report.patient?.id)
            .filter((id): id is string => Boolean(id));

        return Array.from(new Set(patientIds));
    }, [visibleReports]);

    const { data: patientApprovalStatuses = [] } = useQuery({
        queryKey: [
            'publisher-result-imports',
            'patient-approval-statuses',
            visiblePatientIdsWithInProgressReports,
        ],
        queryFn: () =>
            publisherResultImportsApi.getPatientApprovalStatuses(
                visiblePatientIdsWithInProgressReports,
            ),
        enabled:
            publisherResultImportsFeature.enabled &&
            visiblePatientIdsWithInProgressReports.length > 0,
    });

    const patientApprovalStatusById = useMemo(
        () => new Map(patientApprovalStatuses.map((status) => [status.patient_id, status])),
        [patientApprovalStatuses],
    );

    const visibleReportsWithApprovalCounts = useMemo(
        () =>
            visibleReports.map((report) => ({
                ...report,
                pending_publisher_result_approvals_count:
                    publisherResultImportsFeature.enabled &&
                    report.status === 'in_progress' &&
                    report.patient?.id
                        ? (patientApprovalStatusById.get(report.patient.id)?.ready_count ?? 0)
                        : 0,
            })),
        [patientApprovalStatusById, publisherResultImportsFeature.enabled, visibleReports],
    );

    const stats = useMemo<DashboardStats>(
        () => ({
            total: summary?.total ?? 0,
            inProgress: summary?.in_progress ?? 0,
            completed: summary?.completed ?? 0,
            thisMonth: summary?.this_month ?? 0,
        }),
        [summary],
    );

    const isLoading = hasLocalFilters ? isAllReportsLoading : isPaginatedLoading;
    const isFetching = hasLocalFilters ? isAllReportsFetching : isPaginatedFetching;

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, statusFilter]);

    const statusCounts = {
        all: summary?.total ?? 0,
        in_progress: summary?.in_progress ?? 0,
        completed: summary?.completed ?? 0,
    };

    const handleDelete = (reportId: Report['id']) => {
        if (!reportId) return;
        const report = visibleReports.find((item) => item.id === reportId);
        if (!report) return;
        const patientName = report.patient?.full_name;

        confirmDelete({
            title: 'Excluir laudo?',
            description: `Você está prestes a excluir o laudo de ${patientName}. Essa ação não pode ser desfeita.`,
            onConfirm: (close) => {
                deleteReportMutation.mutate(reportId);
                close();
            },
        });
    };

    const handleExport = async (reportId: Report['id']) => {
        if (!reportId) return;

        const report = visibleReports.find((item) => item.id === reportId);
        if (!report || !canExportReport(report)) return;
        const patientName = report.patient?.full_name;

        try {
            toast.info('Iniciando exportação...');
            const blob = await reportsApi.export(reportId);
            downloadBlob(blob, getReportDocxFilename(patientName, reportId));
            queryClient.invalidateQueries({ queryKey: ENTITLEMENTS_QUERY_KEY });
            toast.success('Exportação concluída com sucesso');
        } catch (error) {
            console.error('Failed to export report', error);
            if (getErrorStatus(error) === 403) {
                toast.warning(
                    getEntitlementBlockedMessage(
                        exportReportAccess.decision,
                        'Você já usou sua exportação gratuita. Escolha um plano para exportar outros laudos.',
                    ),
                );
                return;
            }

            toast.error('Erro ao exportar laudo');
        }
    };

    return (
        <PageShell width="full" contentClassName="gap-0 px-0 py-0 sm:px-0 lg:px-0">
            <motion.div
                className="w-full"
                variants={pageVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 pb-5 pt-3 sm:gap-6 sm:px-8 sm:pb-8 sm:pt-4 lg:px-10">
                    <DashboardHero />
                    <DashboardStatsGrid stats={stats} />

                    <DashboardFilters
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        statusFilter={statusFilter}
                        onStatusFilterChange={setStatusFilter}
                        statusCounts={statusCounts}
                        footer={
                            totalPages > 1 ? (
                                <DashboardPagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                    totalReports={totalReports}
                                    itemsPerPage={pageSize}
                                />
                            ) : undefined
                        }
                    >
                        <DashboardReportList
                            reports={visibleReportsWithApprovalCounts}
                            isLoading={isLoading || isFetching}
                            viewMode={viewMode}
                            onDelete={(id) => id && handleDelete(id)}
                            onExport={(id) => id && handleExport(id)}
                        />
                    </DashboardFilters>
                </div>
            </motion.div>
        </PageShell>
    );
}
